import { useMemo, useState } from 'react';
import { BookOpen, FileText, Link2, Plus, Search, Trash2, Video } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { ApiError } from '@/core/api';
import { useCourses } from '@/features/lms/courses/queries';
import { useSession } from '@/stores/session';
import { MaterialFormDialog } from '../components/MaterialFormDialog';
import { LESSON_KINDS, LESSON_KIND_LABEL } from '../constants';
import type { Lesson, LessonInput, LessonKind } from '../api';
import { useCreateLesson, useDeleteLesson, useLessons } from '../queries';

const ALL = 'all';
const HR_ROLES = new Set(['founder', 'super_admin', 'hr']);

const KIND_ICON: Record<LessonKind, LucideIcon> = {
  material: FileText,
  guide: BookOpen,
  video: Video,
  link: Link2,
};

/**
 * The Materials Library is a cross-course view of Lesson records of kind
 * material/guide/video/link. It uses the existing `/lms/lessons/?kind__in=`
 * filter — no separate model.
 */
const MATERIAL_KINDS = LESSON_KINDS.map((k) => k.value).join(',');

interface MaterialsProps {
  /** Restrict the kind filter (e.g. /lms/guides shows only "guide"). */
  defaultKind?: LessonKind;
  pageTitle?: string;
}

export function MaterialsLibraryPage({ defaultKind, pageTitle }: MaterialsProps = {}) {
  const role = useSession((s) => s.role);
  const canManage = role ? HR_ROLES.has(role) : false;

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [kindFilter, setKindFilter] = useState<LessonKind | typeof ALL>(defaultKind ?? ALL);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [toDelete, setToDelete] = useState<Lesson | null>(null);

  const params = useMemo(() => {
    const base: { search?: string; kind?: LessonKind; kind__in?: string } = {
      search: search || undefined,
    };
    if (kindFilter === ALL) base.kind__in = MATERIAL_KINDS;
    else base.kind = kindFilter;
    return base;
  }, [search, kindFilter]);

  const { data, isPending, isError, error, isFetching } = useLessons(params);
  const { data: coursesData } = useCourses({});
  const courseTitles = useMemo(() => {
    const map = new Map<number, string>();
    for (const c of coursesData?.results ?? []) map.set(c.id, c.title);
    return map;
  }, [coursesData]);

  const create = useCreateLesson();
  const remove = useDeleteLesson();

  const materials = data?.results ?? [];

  function applySearch(e: React.FormEvent) {
    e.preventDefault();
    setSearch(searchInput.trim());
  }

  async function handleSubmit(input: LessonInput) {
    await create.mutateAsync(input);
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{pageTitle ?? 'Materials'}</h1>
          <p className="text-muted-foreground text-sm">
            Cross-course learning content — materials, guides, videos and links.
          </p>
        </div>
        {canManage ? (
          <Button onClick={() => setDialogOpen(true)}>
            <Plus className="size-4" /> Add material
          </Button>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <form onSubmit={applySearch} className="relative max-w-sm flex-1">
          <Search className="text-muted-foreground absolute top-2.5 left-2.5 size-4" />
          <Input
            className="pl-8"
            placeholder="Search materials"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </form>
        {defaultKind ? null : (
          <Select
            value={kindFilter}
            onValueChange={(v) => setKindFilter(v as LessonKind | typeof ALL)}
          >
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All kinds</SelectItem>
              {LESSON_KINDS.map((k) => (
                <SelectItem key={k.value} value={k.value}>
                  {k.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {isError ? (
        <p className="text-destructive text-sm">
          {(error as unknown as ApiError)?.message ?? 'Failed to load materials.'}
        </p>
      ) : (
        <div className="rounded-md border" aria-busy={isFetching}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Course</TableHead>
                <TableHead>Kind</TableHead>
                {canManage ? <TableHead className="w-12" /> : null}
              </TableRow>
            </TableHeader>
            <TableBody>
              {isPending ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={canManage ? 4 : 3}>
                      <Skeleton className="h-6 w-full" />
                    </TableCell>
                  </TableRow>
                ))
              ) : materials.length ? (
                materials.map((m) => {
                  const Icon = KIND_ICON[m.kind];
                  return (
                    <TableRow key={m.id}>
                      <TableCell>
                        <div className="flex items-center gap-2 font-medium">
                          <Icon className="text-muted-foreground size-4" />
                          {m.title}
                        </div>
                        {m.content ? (
                          <p className="text-muted-foreground mt-1 line-clamp-2 text-sm">{m.content}</p>
                        ) : null}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {courseTitles.get(m.course) ?? `#${m.course}`}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{LESSON_KIND_LABEL[m.kind]}</Badge>
                      </TableCell>
                      {canManage ? (
                        <TableCell className="text-right">
                          <Button
                            size="icon"
                            variant="ghost"
                            aria-label="Delete material"
                            onClick={() => setToDelete(m)}
                          >
                            <Trash2 className="size-4" />
                          </Button>
                        </TableCell>
                      ) : null}
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={canManage ? 4 : 3} className="text-muted-foreground text-center">
                    No materials in this library.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}

      <MaterialFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSubmit={handleSubmit}
      />

      <AlertDialog open={Boolean(toDelete)} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete material?</AlertDialogTitle>
            <AlertDialogDescription>“{toDelete?.title}” will be removed from its course.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (toDelete) remove.mutate(toDelete.id);
                setToDelete(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
